import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  CircularProgress,
  FormControlLabel,
  Switch,
  Snackbar,
  Chip,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  VolumeUp,
  VolumeOff,
  Star,
  RecordVoiceOver
} from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { Voice, VoiceInput } from '../types';
import { GET_VOICES } from '../graphql/queries';
import { CREATE_VOICE, UPDATE_VOICE, DELETE_VOICE } from '../graphql/mutations';
import { useUserRole } from '../hooks/useUserRole';

const Voices: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [editingVoice, setEditingVoice] = useState<Voice | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [formData, setFormData] = useState<VoiceInput>({
    voiceId: '',
    name: '',
    description: '',
    isDefault: false,
    isActive: true
  });

  const { user, isAdmin: hasControlPanelAccess, isLoading: userLoading } = useUserRole();

  // GraphQL queries and mutations
  const { data: voicesData, loading: voicesLoading, error: voicesError, refetch } = useQuery(GET_VOICES);
  
  const [createVoice, { loading: createLoading }] = useMutation(CREATE_VOICE, {
    onCompleted: (data) => {
      handleClose();
      const message = data?.createVoice?.isDefault 
        ? 'Voice created successfully and set as default! Other voices have been unmarked as default.'
        : 'Voice created successfully!';
      setSnackbar({ open: true, message, severity: 'success' });
      refetch();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: `Error creating voice: ${error.message}`, severity: 'error' });
    }
  });

  const [updateVoice, { loading: updateLoading }] = useMutation(UPDATE_VOICE, {
    onCompleted: (data) => {
      handleClose();
      const message = data?.updateVoice?.isDefault 
        ? 'Voice updated successfully and set as default! Other voices have been unmarked as default.'
        : 'Voice updated successfully!';
      setSnackbar({ open: true, message, severity: 'success' });
      refetch();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: `Error updating voice: ${error.message}`, severity: 'error' });
    }
  });

  const [deleteVoice, { loading: deleteLoading }] = useMutation(DELETE_VOICE, {
    onCompleted: () => {
      setSnackbar({ open: true, message: 'Voice deleted successfully!', severity: 'success' });
      refetch();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: `Error deleting voice: ${error.message}`, severity: 'error' });
    }
  });

  const voices = voicesData?.voices || [];

  // Calculate statistics
  const totalVoices = voices.length;
  const activeVoices = voices.filter((voice: Voice) => voice.isActive).length;
  const inactiveVoices = totalVoices - activeVoices;
  const defaultVoices = voices.filter((voice: Voice) => voice.isDefault).length;

  const filteredVoices = voices.filter((voice: Voice) =>
    voice.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    voice.voiceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (voice.description && voice.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleOpen = (voice?: Voice) => {
    if (voice) {
      setEditingVoice(voice);
      setFormData({
        voiceId: voice.voiceId,
        name: voice.name,
        description: voice.description || '',
        isDefault: voice.isDefault,
        isActive: voice.isActive
      });
    } else {
      setEditingVoice(null);
      setFormData({
        voiceId: '',
        name: '',
        description: '',
        isDefault: false,
        isActive: true
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingVoice(null);
    setFormData({
      voiceId: '',
      name: '',
      description: '',
      isDefault: false,
      isActive: true
    });
  };

  const handleSubmit = () => {
    if (!formData.voiceId || !formData.name) {
      setSnackbar({ open: true, message: 'Voice ID and Name are required', severity: 'error' });
      return;
    }

    if (editingVoice) {
      updateVoice({
        variables: {
          id: editingVoice.id,
          voice: formData
        }
      });
    } else {
      createVoice({
        variables: {
          voice: formData
        }
      });
    }
  };

  const handleDelete = (voice: Voice) => {
    if (window.confirm(`Are you sure you want to delete the voice "${voice.name}"?`)) {
      deleteVoice({
        variables: {
          id: voice.id
        }
      });
    }
  };

  const getStatusChip = (isActive: boolean) => {
    return isActive ? 
      <Chip label="Active" color="success" size="small" icon={<VolumeUp />} /> : 
      <Chip label="Inactive" color="default" size="small" />;
  };

  if (userLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!hasControlPanelAccess) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You don't have permission to access this page. Control Panel access requires role ID ≤ 2.
        </Alert>
      </Container>
    );
  }

  if (voicesError) {
    // Check if this is a permission error
    if (voicesError.message.includes('Insufficient permissions') || voicesError.message.includes('Role ID must be <= 2')) {
      return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="warning">
            <AlertTitle>Access Denied</AlertTitle>
            You need administrator privileges (Role ID ≤ 2) to access voice management.
            <br />
            Current user role: Role {user?.roleId || 'N/A'}
            <br />
            Please contact your system administrator for access.
          </Alert>
        </Container>
      );
    }

    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Error loading voices: {voicesError.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Voices Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpen()}
          disabled={!hasControlPanelAccess}
        >
          Add Voice
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="overline">
                    Total Voices
                  </Typography>
                  <Typography variant="h4">
                    {totalVoices}
                  </Typography>
                </Box>
                <RecordVoiceOver sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="overline">
                    Active Voices
                  </Typography>
                  <Typography variant="h4">
                    {activeVoices}
                  </Typography>
                </Box>
                <VolumeUp sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="overline">
                    Inactive Voices
                  </Typography>
                  <Typography variant="h4">
                    {inactiveVoices}
                  </Typography>
                </Box>
                <VolumeOff sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="overline">
                    Default Voices
                  </Typography>
                  <Typography variant="h4">
                    {defaultVoices}
                  </Typography>
                </Box>
                <Star sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search voices..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
          }}
        />
      </Paper>

      {/* Voices Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Voice ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Default</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {voicesLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredVoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {searchTerm ? 'No voices match your search.' : 'No voices found.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredVoices.map((voice: Voice) => (
                  <TableRow key={voice.id}>
                    <TableCell>{voice.voiceId}</TableCell>
                    <TableCell>{voice.name}</TableCell>
                    <TableCell>{voice.description || '-'}</TableCell>
                    <TableCell>
                      {voice.isDefault ? 
                        <Chip label="Default" color="primary" size="small" /> : 
                        <Chip label="No" color="default" size="small" />}
                    </TableCell>
                    <TableCell>{getStatusChip(voice.isActive)}</TableCell>
                    <TableCell>{new Date(voice.createdDate).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpen(voice)}
                        disabled={!hasControlPanelAccess}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(voice)}
                        disabled={!hasControlPanelAccess}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingVoice ? 'Edit Voice' : 'Create Voice'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Voice ID"
              value={formData.voiceId}
              onChange={(e) => setFormData({ ...formData, voiceId: e.target.value })}
              required
              fullWidth
              helperText="Unique identifier for the voice (used by TTS engine)"
            />
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              helperText="Display name for the voice"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
              helperText="Description of the voice characteristics"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isDefault || false}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
              }
              label="Default Voice"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive || false}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={createLoading || updateLoading}
          >
            {createLoading || updateLoading ? <CircularProgress size={20} /> : 
             editingVoice ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Voices;
